import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import type { CompanySourceRecord, JobAd } from "../../../api/client.ts";
import CountryName from "../../../components/CountryName.tsx";
import ScrollTable from "../../../components/table/ScrollTable.tsx";

interface SplitCompanyDialogProps {
  open: boolean;
  onClose: () => void;
  companyName: string;
  sources: CompanySourceRecord[] | undefined;
  jobAds: JobAd[] | undefined;
  selectedSourceRecords: string[];
  onToggleSourceRecord: (id: string, checked: boolean) => void;
  selectedJobAds: string[];
  onToggleJobAd: (id: string, checked: boolean) => void;
  onSplit: () => void;
  pending: boolean;
  error: boolean;
}

export default function SplitCompanyDialog({
  open,
  onClose,
  companyName,
  sources,
  jobAds,
  selectedSourceRecords,
  onToggleSourceRecord,
  selectedJobAds,
  onToggleJobAd,
  onSplit,
  pending,
  error,
}: SplitCompanyDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Split company: {companyName}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
          Select source records and job ads to move to a new company.
        </Typography>

        {sources && sources.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Source records
            </Typography>
            <ScrollTable sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Source</TableCell>
                  <TableCell>External ID</TableCell>
                  <TableCell>Country</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sources.map((sr) => (
                  <TableRow key={sr.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedSourceRecords.includes(sr.id)}
                        onChange={(e) => onToggleSourceRecord(sr.id, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>{sr.source}</TableCell>
                    <TableCell sx={{ maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {sr.external_id}
                    </TableCell>
                    <TableCell><CountryName code={sr.country} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ScrollTable>
          </>
        )}

        {jobAds && jobAds.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Job ads
            </Typography>
            <ScrollTable>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>Title</TableCell>
                  <TableCell>Company name</TableCell>
                  <TableCell>Source</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobAds.map((ad) => (
                  <TableRow key={ad.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedJobAds.includes(ad.id)}
                        onChange={(e) => onToggleJobAd(ad.id, e.target.checked)}
                      />
                    </TableCell>
                    <TableCell>{ad.title}</TableCell>
                    <TableCell>{ad.company_name}</TableCell>
                    <TableCell>{ad.source}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </ScrollTable>
          </>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to split company
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          color="warning"
          onClick={onSplit}
          loading={pending}
          disabled={selectedSourceRecords.length === 0 && selectedJobAds.length === 0}
        >
          Split
        </Button>
      </DialogActions>
    </Dialog>
  );
}
